/**
 * 卡片冻结/解冻页面
 */

import { useState, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Toast } from 'react-vant'
import { APIWeb } from '@/api/index'
import { useUserInfo, useMerchantConfig, useCardList } from '@/hooks'
import PageLayout from '@/components/PageLayout'
import Header from '@/components/Header'
import Back from '@/components/Back'
import CustomButton from '@/components/CustomButton'
import FixedBottom from '@/components/FixedBottom'

export default function CardFreeze() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const cardId = searchParams.get('id') || ''
  const isFrozen = searchParams.get('frozen') === 'true'
  
  const { userInfo } = useUserInfo()
  const { merchantConfig } = useMerchantConfig()
  const { actions } = useCardList()
  
  const [loading, setLoading] = useState(false)

  // 确认冻结/解冻
  const handleConfirm = useCallback(async () => {
    if (!cardId || !userInfo || !merchantConfig) {
      Toast.info(t('参数错误'))
      return
    }

    const params = {
      card_id: Number(cardId),
      member_id: String(userInfo.id || ''),
      merchant_id: String(merchantConfig.merchant_id || ''),
      verify_key: ''
    }

    try {
      setLoading(true)
      
      if (isFrozen) {
        // 解冻
        await APIWeb.unfreezeVirtualCardV2(params)
        Toast.success(t('解冻成功'))
      } else {
        // 冻结
        await APIWeb.freezeVirtualCardV2(params)
        Toast.success(t('冻结成功'))
      }
      
      // 刷新卡片列表
      actions.refreshCardList()
      // 返回卡片主界面
      navigate('/card', { replace: true })
    } catch (error) {
      console.error('冻结/解冻失败:', error)
    } finally {
      setLoading(false)
    }
  }, [cardId, userInfo, merchantConfig, isFrozen, actions, navigate, t])

  return (
    <PageLayout>
      <Header
        leftChildren={<Back />}
      />

      <div className="flex-1 flex flex-col items-center pt-20 px-5">
        {/* 图标 */}
        <div className="w-24 h-24 rounded-full bg-[#F0F2FF] flex items-center justify-center">
          <div className="w-18 h-18 rounded-full bg-[var(--primary-color)] flex items-center justify-center">
            <img src="/images/card-v2/freeze-white.svg" alt="" />
          </div>
        </div>

        {/* 标题 */}
        <div className="text-xl font-semibold text-[#1D2129] mt-6">
          {isFrozen ? t('解冻卡片') : t('冻结提醒')}
        </div>

        {/* 描述列表 */}
        <div className="mt-3 w-full px-5">
          {isFrozen ? (
            // 解冻提示
            <ul className="space-y-2 text-base text-[#667085]">
              <li className="flex items-start">
                <span className="w-1.5 h-1.5 rounded-full bg-[#667085] mt-2 mr-3 flex-shrink-0" />
                <span>{t('这将恢复所有线上、线下及ATM交易')}</span>
              </li>
              <li className="flex items-start">
                <span className="w-1.5 h-1.5 rounded-full bg-[#667085] mt-2 mr-3 flex-shrink-0" />
                <span>{t('您可以随时冻结卡片暂停使用')}</span>
              </li>
            </ul>
          ) : (
            // 冻结提示
            <ul className="space-y-2 text-base text-[#667085]">
              <li className="flex items-start">
                <span className="w-1.5 h-1.5 rounded-full bg-[#667085] mt-2 mr-3 flex-shrink-0" />
                <span>{t('这将暂停所有线上、线下及ATM交易')}</span>
              </li>
              <li className="flex items-start">
                <span className="w-1.5 h-1.5 rounded-full bg-[#667085] mt-2 mr-3 flex-shrink-0" />
                <span>{t('冻结后使用卡片将产生拒绝交易费')}</span>
              </li>
              <li className="flex items-start">
                <span className="w-1.5 h-1.5 rounded-full bg-[#667085] mt-2 mr-3 flex-shrink-0" />
                <span>{t('您可以随时解冻以恢复使用')}</span>
              </li>
            </ul>
          )}
        </div>
      </div>

      {/* 底部按钮 */}
      <FixedBottom>
        <CustomButton
          onClick={handleConfirm}
          loading={loading}
        >
          {isFrozen ? t('继续解冻') : t('确认冻结')}
        </CustomButton>
      </FixedBottom>
    </PageLayout>
  )
}

